/**
 * useWebSocketChannel - React Hook for Channel Subscriptions
 * 
 * Provides easy-to-use channel subscriptions with automatic cleanup.
 * Manages connection lifecycle and handles reconnection gracefully.
 * 
 * @example Basic Usage
 * ```tsx
 * function EmailNotifications() {
 *   const { status, subscribe, unsubscribe } = useWebSocketChannel();
 *   
 *   useEffect(() => {
 *     const unsub = subscribe('email:user:245', (data) => {
 *       console.log('Email:', data);
 *     });
 *     return unsub;
 *   }, [subscribe]);
 *   
 *   return <div>Status: {status}</div>;
 * }
 * ```
 * 
 * @example With auto-subscription
 * ```tsx
 * function EmailNotifications({ userId }: { userId: number }) {
 *   const { data, status } = useWebSocketChannel({
 *     channel: `email:user:${userId}`,
 *     autoConnect: true,
 *   });
 *   
 *   return <div>{JSON.stringify(data)}</div>;
 * }
 * ```
 * 
 * @created February 7, 2026
 * @phase B4 - Client SDK Implementation
 */

'use client';

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { isIntentionalDisconnectError, wsClient } from '@/lib/websocket';
import type { ConnectionStatus, ChannelCallback, SubscribeResult } from '@/lib/websocket';

// ============================================================================
// TYPES
// ============================================================================

export interface UseWebSocketChannelOptions {
  /** 
   * Channel to auto-subscribe to on mount 
   * If provided, automatically subscribes and unsubscribes on cleanup
   */
  channel?: string;
  
  /**
   * Auto-connect on mount (default: false)
   * ClientLayout already bootstraps wsClient.connect() globally,
   * so hooks don't need to duplicate connect calls.
   */
  autoConnect?: boolean;
  
  /**
   * Maximum number of messages to retain in the messages array.
   * Prevents unbounded memory growth on high-traffic channels.
   * Default: 100. Set to 0 for unlimited (not recommended).
   */
  maxMessages?: number;

  /**
   * Whether to retain received messages in the hook-level messages array.
   * Disable this for trigger-only subscriptions to reduce memory churn.
   * Default: true.
   */
  retainMessages?: boolean;

  /**
   * Whether to retain the most recent payload in the hook-level data state.
   * Disable this for trigger-only subscriptions that only react via callbacks.
   * Default: true.
   */
  retainLatestData?: boolean;
  
  /**
   * Callback when channel data arrives (alternative to using data state)
   */
  onMessage?: ChannelCallback;
  
  /**
   * Callback when connection status changes
   */
  onStatusChange?: (status: ConnectionStatus) => void;
  
  /**
   * Callback when subscription fails
   */
  onError?: (error: string) => void;
}

export interface UseWebSocketChannelReturn<T = unknown> {
  /** Current connection status */
  status: ConnectionStatus;
  
  /** Most recent data received on the auto-subscribed channel */
  data: T | null;
  
  /** All messages received (for channels that need history) */
  messages: T[];
  
  /** Whether currently connected */
  isConnected: boolean;
  
  /** Whether auto-subscribed channel is active */
  isSubscribed: boolean;
  
  /** Error message if subscription failed */
  error: string | null;
  
  /** Subscribe to a channel manually */
  subscribe: (channel: string, callback: ChannelCallback) => () => void;
  
  /** Unsubscribe from a channel */
  unsubscribe: (channel: string) => void;
  
  /** Publish data to a channel */
  publish: (channel: string, data: unknown) => Promise<boolean>;
  
  /** Manually connect */
  connect: () => Promise<void>;
  
  /** Manually disconnect */
  disconnect: () => void;
  
  /** Clear stored messages */
  clearMessages: () => void;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function useWebSocketChannel<T = unknown>(
  options: UseWebSocketChannelOptions = {}
): UseWebSocketChannelReturn<T> {
  const { 
    channel, 
    autoConnect = false, 
    maxMessages = 100,
    retainMessages = true,
    retainLatestData = true,
    onMessage, 
    onStatusChange,
    onError,
  } = options;
  
  // State
  const [status, setStatus] = useState<ConnectionStatus>(wsClient.getStatus());
  const [data, setData] = useState<T | null>(null);
  const [messages, setMessages] = useState<T[]>([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stableChannel = channel ?? null;
  
  // Refs for callbacks to avoid stale closures
  const onMessageRef = useRef(onMessage);
  const onStatusChangeRef = useRef(onStatusChange);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  
  // Track subscription cleanup
  const unsubscribeRef = useRef<(() => void) | null>(null);
  
  // Connection status listener
  useEffect(() => {
    const unsubStatus = wsClient.onConnection((newStatus) => {
      setStatus(newStatus);
      onStatusChangeRef.current?.(newStatus);
    });
    
    return unsubStatus;
  }, []);
  
  // Auto-connect
  useEffect(() => {
    if (!autoConnect) return;
    
    if (wsClient.getStatus() === 'disconnected') {
      wsClient.connect().catch((err) => {
        if (!isIntentionalDisconnectError(err)) {
          console.error('[useWebSocketChannel] Auto-connect failed:', err);
          setError(String(err));
        }
      });
    }
  }, [autoConnect]);
  
  // Auto-subscribe to channel
  useEffect(() => {
    if (!stableChannel) return;
    
    // Wait for connection
    if (status !== 'connected') {
      return;
    }

    queueMicrotask(() => {
      setError(null);
    });
    
    const handleMessage: ChannelCallback = (msgData, ch) => {
      const typedData = msgData as T;
      if (retainLatestData) {
        setData(typedData);
      }
      if (retainMessages) {
        setMessages(prev => {
          const next = [...prev, typedData];
          // Cap messages array to prevent unbounded memory growth
          return maxMessages > 0 && next.length > maxMessages
            ? next.slice(-maxMessages)
            : next;
        });
      }
      onMessageRef.current?.(msgData, ch);
    };
    
    // Subscribe using the low-level subscribe() which returns a per-callback
    // unsubscribe function. This ensures cleanup only removes THIS hook's
    // callback, not all listeners on the channel (which wsClient.unsubscribe()
    // would do, breaking other components on the same channel).
    const perCallbackUnsub = wsClient.subscribe(stableChannel, handleMessage);
    
    // Handle the async subscription result (server acknowledgement)
    const subscription = wsClient.getChannelSubscription?.(stableChannel);
    if (subscription?.subscribePromise) {
      subscription.subscribePromise
        .then((result: SubscribeResult) => {
          if (result.success) {
            setIsSubscribed(true);
            if (retainLatestData && result.initialData !== undefined) {
              setData(result.initialData as T);
            }
          } else {
            setError(result.error || 'Subscription failed');
            onErrorRef.current?.(result.error || 'Subscription failed');
          }
        })
        .catch((err: unknown) => {
          setError(String(err));
          onErrorRef.current?.(String(err));
        });
    } else {
      // Already subscribed or not connected yet (will subscribe on connect)
      queueMicrotask(() => {
        setIsSubscribed(true);
      });
    }
    
    unsubscribeRef.current = () => {
      perCallbackUnsub();
      setIsSubscribed(false);
    };
    
    return () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, [maxMessages, retainLatestData, retainMessages, stableChannel, status]);
  
  // Manual subscribe
  const subscribe = useCallback((ch: string, callback: ChannelCallback): () => void => {
    return wsClient.subscribe(ch, callback);
  }, []);
  
  // Manual unsubscribe
  const unsubscribe = useCallback((ch: string): void => {
    wsClient.unsubscribe(ch);
  }, []);
  
  // Publish
  const publish = useCallback(async (ch: string, publishData: unknown): Promise<boolean> => {
    return wsClient.publish(ch, publishData);
  }, []);
  
  // Connect
  const connect = useCallback(async (): Promise<void> => {
    await wsClient.connect();
  }, []);
  
  // Disconnect
  const disconnect = useCallback((): void => {
    wsClient.disconnect();
  }, []);
  
  // Clear messages
  const clearMessages = useCallback((): void => {
    setMessages([]);
    setData(null);
  }, []);
  
  // Memoize return value
  return useMemo(() => ({
    status,
    data,
    messages,
    isConnected: status === 'connected',
    isSubscribed,
    error,
    subscribe,
    unsubscribe,
    publish,
    connect,
    disconnect,
    clearMessages,
  }), [
    status,
    data,
    messages,
    isSubscribed,
    error,
    subscribe,
    unsubscribe,
    publish,
    connect,
    disconnect,
    clearMessages,
  ]);
}

// ============================================================================
// SPECIALIZED HOOKS
// ============================================================================

/**
 * Hook for connecting to WebSocket without subscribing to any channel
 * Useful for components that just need to show connection status
 */
export function useWebSocketStatus(): {
  status: ConnectionStatus;
  isConnected: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
} {
  const { status, isConnected, connect, disconnect } = useWebSocketChannel({
    autoConnect: true,
  });
  
  return { status, isConnected, connect, disconnect };
}

/**
 * Hook for subscribing to multiple channels at once
 * @param channels - Array of channel names to subscribe to
 * @param onMessage - Callback when any channel receives a message
 */
export function useMultiChannelSubscription(
  channels: string[],
  onMessage: (data: unknown, channel: string) => void
): {
  status: ConnectionStatus;
  isConnected: boolean;
} {
  const { status, isConnected, subscribe } = useWebSocketChannel({ autoConnect: true });
  const stableChannels = useMemo(() => [...channels], [channels]);
  
  const onMessageRef = useRef(onMessage);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  
  useEffect(() => {
    if (status !== 'connected') return;
    
    const unsubscribes = stableChannels.map(channel => 
      subscribe(channel, (data, ch) => onMessageRef.current(data, ch))
    );
    
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [stableChannels, status, subscribe]);
  
  return { status, isConnected };
}
